'use client';

import {
  CaretDownIcon,
  BookOpenIcon,
  BrainIcon,
  GearSixIcon,
  HeartIcon,
  SignOutIcon,
  UserIcon,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import Link from 'next/link';
import { useEffect, useState } from 'react';

import { Badge, MOBILE_MENU_ROW_CLASS } from '../../lib/header-constants';
import { cn } from '@/shared/utils/cn';
import { useNavbarContext, type NavbarAccountLink, type NavbarUser } from './context';

interface HeaderMobileUserSectionProps {
  isDrawerOpen: boolean;
  onClose: () => void;
  user: NavbarUser;
}

const ICON_MAP: Record<NonNullable<NavbarAccountLink['icon']>, PhosphorIcon> = {
  book: BookOpenIcon,
  brain: BrainIcon,
  gear: GearSixIcon,
  heart: HeartIcon,
  user: UserIcon,
};

const HeaderMobileUserSection = ({ isDrawerOpen, onClose, user }: HeaderMobileUserSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { accountLinks, onLogout } = useNavbarContext();

  useEffect(() => {
    if (!isDrawerOpen) setIsExpanded(false);
  }, [isDrawerOpen]);

  const handleLogout = async () => {
    onClose();
    await onLogout?.();
  };

  const initial = user.name.charAt(0).toUpperCase();

  return (
    <div className="border-adipa-border border-b pb-3">
      <button
        aria-expanded={isExpanded}
        className={cn(
          'rounded-adipa-lg flex w-full items-center gap-3 px-1 py-2',
          'duration-adipa-fast hover:bg-adipa-surface-lavender transition-colors ease-out',
          'focus-visible:outline-adipa-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        )}
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
      >
        <span
          aria-hidden="true"
          className="rounded-adipa-full bg-adipa-primary text-adipa-primary-foreground flex h-9 w-9 shrink-0 items-center justify-center text-sm font-bold"
        >
          {initial}
        </span>
        <div className="flex-1 text-left">
          <p className="text-adipa-text-primary text-sm font-semibold">{user.name}</p>
          {user.email ? <p className="text-adipa-text-secondary text-xs">{user.email}</p> : null}
        </div>
        <CaretDownIcon
          aria-hidden="true"
          className={cn(
            'text-adipa-text-secondary duration-adipa-base shrink-0 transition-transform ease-out',
            isExpanded && 'rotate-180',
          )}
          size={16}
          weight="bold"
        />
      </button>

      <div
        className={cn(
          'mobile-accordion-panel',
          isExpanded ? 'mobile-accordion-panel-open' : 'mobile-accordion-panel-closed',
        )}
      >
        <ul className="overflow-hidden">
          {accountLinks.map(({ badge, badgeColor, href, icon, label }) => {
            const Icon = icon ? ICON_MAP[icon] : null;

            return (
              <li key={href}>
                <Link
                  className={cn(MOBILE_MENU_ROW_CLASS, 'flex items-center gap-3 whitespace-nowrap')}
                  href={href}
                  onClick={onClose}
                >
                  {Icon ? <Icon aria-hidden="true" size={18} weight="regular" /> : null}
                  {label}
                  {badge ? <Badge color={badgeColor ?? 'magenta'} label={badge} /> : null}
                </Link>
              </li>
            );
          })}
          <li>
            <button
              className={cn(
                MOBILE_MENU_ROW_CLASS,
                'text-adipa-error hover:bg-adipa-error-surface hover:text-adipa-error flex items-center gap-3',
              )}
              type="button"
              onClick={handleLogout}
            >
              <SignOutIcon aria-hidden="true" size={18} weight="regular" />
              Cerrar sesión
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default HeaderMobileUserSection;
